import React, { useState } from 'react';
import { FileCode, GitBranch, GitCommit, Calendar, User } from 'lucide-react';

/**
 * Enhanced Flow Chart Node with AI Summary and Git Metadata
 *
 * Shows:
 * - Filename + one-line AI summary
 * - Color-coded by impact score
 * - Hover tooltip with full details
 */
function FlowChartNode({ file, onClick }) {
  const [showTooltip, setShowTooltip] = useState(false);

  // Determine node color based on impact score
  const getNodeColor = (impactScore) => {
    if (impactScore >= 80) return 'bg-red-500/20 border-red-500/50';
    if (impactScore >= 60) return 'bg-orange-500/20 border-orange-500/50';
    if (impactScore >= 40) return 'bg-yellow-500/20 border-yellow-500/50';
    if (impactScore >= 20) return 'bg-blue-500/20 border-blue-500/50';
    return 'bg-green-500/20 border-green-500/50';
  };

  const getRiskBadge = (impactScore) => {
    if (impactScore >= 80) return { text: 'CRITICAL', color: 'text-red-400', icon: '🚨' };
    if (impactScore >= 60) return { text: 'HIGH', color: 'text-orange-400', icon: '⚠️' };
    if (impactScore >= 40) return { text: 'MEDIUM', color: 'text-yellow-400', icon: '⚡' };
    if (impactScore >= 20) return { text: 'LOW', color: 'text-blue-400', icon: '✓' };
    return { text: 'MINIMAL', color: 'text-green-400', icon: '✓' };
  };

  const badge = getRiskBadge(file.impact_score);
  const filename = file.file_path.split('/').pop();
  const directory = file.file_path.substring(0, file.file_path.lastIndexOf('/'));

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return date.toLocaleDateString();
  };

  return (
    <div
      className="relative"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      {/* Main Node */}
      <div
        onClick={() => onClick(file)}
        className={`
          relative min-w-[200px] max-w-[280px] p-4 rounded-lg border-2 cursor-pointer
          transition-all duration-200 hover:scale-105 hover:shadow-lg
          ${getNodeColor(file.impact_score)}
        `}
      >
        {/* File Icon & Name */}
        <div className="flex items-start gap-2 mb-2">
          <FileCode className="w-5 h-5 text-[#94a3b8] flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-white text-sm truncate" title={filename}>
              {filename}
            </div>
            {directory && (
              <div className="text-xs text-[#64748b] truncate" title={directory}>
                {directory}
              </div>
            )}
          </div>
        </div>

        {/* One-Line AI Summary */}
        {file.one_line_summary && (
          <div className="text-xs text-[#94a3b8] italic mb-2 line-clamp-1">
            "{file.one_line_summary}"
          </div>
        )}

        {/* Impact Score & Badge */}
        <div className="flex items-center justify-between text-xs">
          <span className={`font-semibold ${badge.color}`}>
            {badge.icon} {badge.text}
          </span>
          <span className="text-[#64748b]">
            Impact: {file.impact_score?.toFixed(0)}
          </span>
        </div>

        {/* Dependency indicators */}
        <div className="flex items-center gap-3 mt-2 text-xs text-[#64748b]">
          <span title="Dependencies">
            {file.dependencies?.length || 0} →
          </span>
          <span title="Dependents">
            ← {file.dependents?.length || 0}
          </span>
        </div>
      </div>

      {/* Hover Tooltip */}
      {showTooltip && (
        <div className="absolute left-full ml-4 top-0 z-50 w-96 p-4 bg-[#0f172a] border border-[#334155] rounded-lg shadow-2xl">
          {/* File Header */}
          <div className="mb-3 pb-3 border-b border-[#334155]">
            <div className="font-semibold text-white mb-1">{file.file_path}</div>
            <div className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold ${badge.color}`}>
              {badge.icon} {badge.text} RISK
            </div>
          </div>

          {/* AI Description */}
          {file.ai_description && (
            <div className="mb-3 pb-3 border-b border-[#334155]">
              <div className="text-xs font-semibold text-[#94a3b8] mb-1">Description</div>
              <div className="text-sm text-white leading-relaxed">
                {file.ai_description}
              </div>
            </div>
          )}

          {/* Metrics Grid */}
          <div className="grid grid-cols-2 gap-3 mb-3 pb-3 border-b border-[#334155]">
            <div>
              <div className="text-xs text-[#64748b] mb-1">Impact Score</div>
              <div className={`text-lg font-bold ${badge.color}`}>
                {file.impact_score?.toFixed(1)}
              </div>
            </div>
            <div>
              <div className="text-xs text-[#64748b] mb-1">Complexity</div>
              <div className="text-lg font-bold text-[#3b82f6]">
                {file.complexity_score?.toFixed(1)}
              </div>
            </div>
            <div>
              <div className="text-xs text-[#64748b] mb-1">Dependencies</div>
              <div className="text-lg font-bold text-white">
                {file.dependencies?.length || 0}
              </div>
            </div>
            <div>
              <div className="text-xs text-[#64748b] mb-1">Dependents</div>
              <div className="text-lg font-bold text-white">
                {file.dependents?.length || 0}
              </div>
            </div>
          </div>

          {/* Main Exports */}
          {file.main_exports && file.main_exports.length > 0 && (
            <div className="mb-3 pb-3 border-b border-[#334155]">
              <div className="text-xs font-semibold text-[#94a3b8] mb-2">Main Exports</div>
              <div className="flex flex-wrap gap-1">
                {file.main_exports.map((exp, idx) => (
                  <span key={idx} className="px-2 py-1 bg-[#1e293b] text-xs text-white rounded font-mono">
                    {exp}()
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Git Metadata */}
          <div className="space-y-2">
            <div className="text-xs font-semibold text-[#94a3b8] mb-2">Git Information</div>

            {/* Branch */}
            {file.git_branch && (
              <div className="flex items-center gap-2 text-xs">
                <GitBranch className="w-3 h-3 text-[#64748b]" />
                <span className="text-[#64748b]">Branch:</span>
                <span className={`px-2 py-0.5 rounded font-mono ${
                  file.git_branch === 'main' || file.git_branch === 'master'
                    ? 'bg-green-500/20 text-green-400'
                    : 'bg-yellow-500/20 text-yellow-400'
                }`}>
                  {file.git_branch}
                </span>
              </div>
            )}

            {/* Last Commit */}
            {file.git_last_commit_sha && (
              <div className="flex items-start gap-2 text-xs">
                <GitCommit className="w-3 h-3 text-[#64748b] mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <div className="text-[#64748b] mb-1">Latest Commit:</div>
                  <div className="text-white font-mono text-xs mb-1">
                    {file.git_last_commit_sha.substring(0, 7)}
                  </div>
                  {file.git_last_commit_message && (
                    <div className="text-[#94a3b8] italic line-clamp-2">
                      "{file.git_last_commit_message}"
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Author & Date */}
            <div className="flex items-center justify-between text-xs">
              {file.git_last_commit_author && (
                <div className="flex items-center gap-1">
                  <User className="w-3 h-3 text-[#64748b]" />
                  <span className="text-[#94a3b8]">
                    {file.git_last_commit_author.split('<')[0].trim()}
                  </span>
                </div>
              )}
              {file.git_last_commit_date && (
                <div className="flex items-center gap-1">
                  <Calendar className="w-3 h-3 text-[#64748b]" />
                  <span className="text-[#94a3b8]">
                    {formatDate(file.git_last_commit_date)}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Lines of Code */}
          <div className="mt-3 pt-3 border-t border-[#334155] text-xs text-[#64748b]">
            {file.lines_of_code} lines of code
          </div>
        </div>
      )}
    </div>
  );
}

export default FlowChartNode;
